import * as THREE from 'three';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import type { CombatVisualEvent, ProjectileState } from '../../../shared/game/combat-types.js';
import type { CastState } from '../../../shared/game/class-types.js';
import { shepherdSpells } from '../../../content/spells/effects.js';
import { RabbitModel, restingRabbit, type RabbitAction } from '../critters/rabbit-model.js';
import { releaseCritter } from '../critters/critter-parts.js';
import { ownFigureMaterials, shareFigureMaterials } from '../entities/figure-materials.js';
import { disposeEffect, effectMaterial, effectParticles, pathRibbon, shadeEffect, spiralRibbon } from './effect-surfaces.js';
import type { SpellLight } from './spell-effects.js';

const { radiant: RADIANT, necrotic: NECROTIC, spirit: SPIRIT, entice: ENTICE, withered: WITHERED } = shepherdSpells;
const TAU = Math.PI * 2, X = new THREE.Vector3(1, 0, 0);
const additive = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false } as const;
/** Which colour of light gathers in the Shepherd's hand while each spell is cast. */
const CAST_LIGHT: Readonly<Record<string, number>> = { rebuke: RADIANT.glow, 'true-strike': RADIANT.glow, 'withering-touch': NECROTIC.glow, entice: ENTICE.color };

/**
 * Gold light laid over a weapon's own surface, slightly proud of it: additive, so steel and wood read as glowing gold, brightest at the
 * edges. Held weapons are folded into the figure's shared prop draw (`prop-folding.ts`), so this rides each part's own mesh instead.
 */
export function giltMaterial(color: number) {
  return new THREE.ShaderMaterial({ ...additive,
    uniforms: { uColor: { value: new THREE.Color(color) }, uOpacity: { value: 0 } },
    vertexShader: `varying vec3 vNormal; varying vec3 vView;
      void main() { vec4 view = modelViewMatrix * vec4(position + normal * .004, 1.0); vView = -view.xyz; vNormal = normalize(normalMatrix * normal); gl_Position = projectionMatrix * view; }`,
    fragmentShader: `uniform vec3 uColor; uniform float uOpacity; varying vec3 vNormal; varying vec3 vView;
      void main() { float rim = 1.0 - abs(dot(normalize(vNormal), normalize(vView))); gl_FragColor = vec4(uColor, (.55 + rim * .45) * uOpacity); }`,
  });
}
/** A soft ball of light: bright in the middle and gone at the rim, so it reads as glow round its subject rather than a shell. */
function glowMaterial(color: number) {
  return new THREE.ShaderMaterial({ ...additive,
    uniforms: { uColor: { value: new THREE.Color(color) }, uOpacity: { value: 1 } },
    vertexShader: `varying vec3 vNormal; varying vec3 vView;
      void main() { vec4 view = modelViewMatrix * vec4(position, 1.0); vView = -view.xyz; vNormal = normalize(normalMatrix * normal); gl_Position = projectionMatrix * view; }`,
    fragmentShader: `uniform vec3 uColor; uniform float uOpacity; varying vec3 vNormal; varying vec3 vView;
      void main() { float facing = max(0.0, dot(normalize(vNormal), normalize(vView))); gl_FragColor = vec4(uColor, pow(facing, 3.0) * .55 * uOpacity); }`,
  });
}
/** A shaft of light along local +x (0 to 1): a hot core in its colour, bright at one end (`flip` 0: the far end) and gone at the other. */
function streakMaterial(color: number, accent: number, flip = 0) {
  return new THREE.ShaderMaterial({ ...additive, side: THREE.DoubleSide,
    uniforms: { uColor: { value: new THREE.Color(color) }, uAccent: { value: new THREE.Color(accent) }, uOpacity: { value: 1 }, uFlip: { value: flip } },
    vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform vec3 uColor; uniform vec3 uAccent; uniform float uOpacity; uniform float uFlip; varying vec2 vUv;
      void main() {
        float x = mix(vUv.x, 1.0 - vUv.x, uFlip), core = exp(-pow((vUv.y - .5) * 5.5, 2.0));
        float along = pow(x, 1.6) * (1.0 - smoothstep(.92, 1.0, x));
        gl_FragColor = vec4(mix(uColor, uAccent, core * core) * (1.0 + core), core * along * uOpacity);
      }`,
  });
}
/** A flat disc of light on the ground: a soft pool (`band` 0) or a thin ring at its rim (`band` 1). */
function discMaterial(color: number, band: number) {
  return new THREE.ShaderMaterial({ ...additive, side: THREE.DoubleSide,
    uniforms: { uColor: { value: new THREE.Color(color) }, uOpacity: { value: 1 }, uBand: { value: band } },
    vertexShader: `varying float vR; void main() { vR = length(position.xy); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform vec3 uColor; uniform float uOpacity; uniform float uBand; varying float vR;
      void main() { float pool = pow(max(0.0, 1.0 - vR), 2.2) * .5, ring = exp(-pow((vR - .86) * 13.0, 2.0)) * (1.0 - smoothstep(.97, 1.0, vR));
        gl_FragColor = vec4(uColor, mix(pool, ring, uBand) * uOpacity); }`,
  });
}
export function glowBall(color: number, radius: number) { return new THREE.Mesh(new THREE.SphereGeometry(radius, 18, 12), glowMaterial(color)); }
/** Two crossed planes carrying `streakMaterial`, so the shaft has body from any camera angle. Aim it with `aim`. */
export function streak(color: number, accent: number, flip = 0) {
  const group = new THREE.Group();
  for (let i = 0; i < 2; i++) {
    const geometry = new THREE.PlaneGeometry(1, 1); geometry.translate(.5, 0, 0);
    const mesh = new THREE.Mesh(geometry, streakMaterial(color, accent, flip)); mesh.rotation.x = i * Math.PI / 2; mesh.frustumCulled = false; group.add(mesh);
  }
  return group;
}
export function aim(group: THREE.Object3D, from: THREE.Vector3, to: THREE.Vector3, width: number) {
  const along = new THREE.Vector3().subVectors(to, from), length = along.length();
  group.position.copy(from); if (length > 1e-4) group.quaternion.setFromUnitVectors(X, along.divideScalar(length));
  group.scale.set(Math.max(1e-4, length), width, width);
}
export function disc(color: number, band: number) {
  const mesh = new THREE.Mesh(new THREE.CircleGeometry(1, 48), discMaterial(color, band)); mesh.rotation.x = -Math.PI / 2; mesh.frustumCulled = false; return mesh;
}
/** Seeded directions for a burst's rays, spread round the target but kept mostly level, so they read from the isometric camera. */
export function rayDirections(count: number, seed: number) {
  return Array.from({ length: count }, (_, i) => {
    const angle = (i + .5) / count * TAU + seed * 1.37, lift = Math.sin(seed * 3.1 + i * 2.3) * .55;
    return new THREE.Vector3(Math.cos(angle), lift, Math.sin(angle)).normalize();
  });
}

/** Seconds the spirit takes to gather out of light when summoned, and to fade back into it when the fight ends. */
const APPEAR = .7, LEAVE = .8;
/** How far one hop carries it, in metres at its drawn size. */
const HOP_LENGTH = .55;
/** The spirit's timings, for drawing it outside a fight (the explorer). */
export const SPIRIT_MOTION = { appear: APPEAR, leave: LEAVE, hopLength: HOP_LENGTH } as const;
/** How much light it sheds on what is round it, as a spell light's `output`. */
export const spiritLightOutput = (presence: number, flare: number) => presence * (.12 + flare * .12);

/**
 * The spirit's coat: light given a rabbit's shape. It keeps the rabbit's markings only as shading (eyes and ear tips stay dim, so the
 * face reads) and burns brightest at its silhouette, so its form shows instead of a white blot.
 */
function spiritMaterial() {
  const material = new THREE.MeshStandardMaterial({ vertexColors: true, color: SPIRIT.body, emissive: SPIRIT.glow, emissiveIntensity: .55, roughness: .9, flatShading: true, transparent: true, opacity: .92 });
  const uniforms = { uBody: { value: new THREE.Color(SPIRIT.body) }, uRim: { value: new THREE.Color(SPIRIT.rim) }, uRimStrength: { value: 1.4 } };
  material.userData.spirit = uniforms;
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, uniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uBody; uniform vec3 uRim; uniform float uRimStrength;\nfloat spiritMark;')
      .replace('#include <color_fragment>', '#include <color_fragment>\n  spiritMark = mix(.2, 1.0, smoothstep(.05, .16, dot(vColor.rgb, vec3(.3, .59, .11))));\n  diffuseColor.rgb = uBody * spiritMark * .35;')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n  totalEmissiveRadiance *= spiritMark;\n  totalEmissiveRadiance += uRim * pow(1.0 - saturate(dot(normal, normalize(vViewPosition))), 2.2) * uRimStrength;');
  };
  material.customProgramCacheKey = () => 'lifebinding-spirit';
  return material;
}

/** The Lifebinding Spirit as drawn: its rabbit of light, the halo round it, the pool under it, its motes and the streak of a Harm leap (kept apart from `root`). */
export interface SpiritFigure {
  root: THREE.Group; model: RabbitModel; body: THREE.MeshStandardMaterial; halo: THREE.Mesh; pool: THREE.Mesh; motes: THREE.Points; trail: THREE.Group; action: RabbitAction;
}
interface Spirit extends SpiritFigure {
  heroId: string; heading: number; born: number; left?: number;
  /** A Harm leap under way: where it sprang from. */
  dart?: { shotId: number; from: THREE.Vector3 };
}

/** Builds the spirit's figure, at rest and unplaced; `seed` offsets its idle clock. The game's `ShepherdEffects` and the explorer both draw it. */
export function createSpiritFigure(seed: number): SpiritFigure {
  const model = new RabbitModel(0, seed), root = new THREE.Group(); root.name = 'lifebinding-spirit';
  // Its own luminous coat in place of the fur: the spirit is light, so it casts no shadow.
  const body = spiritMaterial();
  model.root.traverse(node => { if (node instanceof THREE.SkinnedMesh) { node.material = body; node.castShadow = false; node.receiveShadow = false; } });
  model.root.scale.setScalar(SPIRIT.scale); root.add(model.root);
  const halo = glowBall(SPIRIT.glow, .38); halo.position.set(0, .24, .05); halo.scale.set(1, .85, 1.35); root.add(halo);
  // A warm pool on the grass under it, whatever the spell-light setting.
  const pool = disc(SPIRIT.glow, 0); pool.position.y = .03; pool.scale.setScalar(.85); root.add(pool);
  const motes = effectParticles(SPIRIT.light, 9, .32, .55); root.add(motes);
  const trail = streak(SPIRIT.glow, RADIANT.core); trail.visible = false;
  return { root, model, body, halo, pool, motes, trail, action: restingRabbit() };
}
/**
 * Poses and lights the figure from its action. `presence` is how far it has gathered out of light (0 gone, 1 whole), `flare` how hard it
 * gathers itself for a power, `pulse` its slow breath of light and `above` how high it is off the ground, in metres.
 */
export function shadeSpirit(figure: SpiritFigure, presence: number, flare: number, pulse: number, above: number, time: number, ppm: number) {
  const { root, body, halo, pool, motes } = figure;
  figure.model.apply(figure.action);
  body.opacity = .92 * presence;
  body.emissiveIntensity = (1.1 + flare * .5) * pulse;
  (body.userData.spirit as { uRimStrength: { value: number } }).uRimStrength.value = (1.6 + flare * .9) * pulse * presence;
  root.scale.setScalar(.6 + .4 * presence);
  (halo.material as THREE.ShaderMaterial).uniforms.uOpacity.value = presence * (.55 * pulse + flare * .45);
  // The pool stays on the grass under a leap, fading as the spirit rises from it.
  pool.position.y = .03 - above / root.scale.y;
  (pool.material as THREE.ShaderMaterial).uniforms.uOpacity.value = presence * THREE.MathUtils.clamp(1 - above / .8, 0, 1) * (.55 + flare * .35) * pulse;
  shadeEffect(motes, time, presence * (.6 + flare * .4), (time * .3) % 1, ppm);
  motes.rotation.y = time * .6;
}
/** Lays the Harm leap's streak of gold from `from` to `to` (world points, already lifted to the spirit's chest). */
export function streakSpirit(figure: SpiritFigure, from: THREE.Vector3, to: THREE.Vector3, opacity: number, time: number) {
  aim(figure.trail, from, to, .3); figure.trail.visible = true; shadeEffect(figure.trail, time, opacity);
}
export function releaseSpiritFigure(figure: SpiritFigure) {
  figure.root.removeFromParent(); figure.trail.removeFromParent();
  releaseCritter(figure.model.root); figure.body.dispose();
  disposeEffect(figure.halo); disposeEffect(figure.pool); disposeEffect(figure.motes); disposeEffect(figure.trail);
}
/** Light gathering in the Shepherd's hand while a spell is cast; it flares and goes as the spell leaves. */
interface Gather { group: THREE.Group; core: THREE.Mesh; motes: THREE.Points; color: number; progress: number; ended?: number }
type FlightKind = 'rebuke' | 'entice' | 'guide';
interface Flight {
  kind: FlightKind; actorId: string; targetId: string; group: THREE.Group; head: THREE.Mesh; from: THREE.Vector3; to: THREE.Vector3; p: number;
  /** Rebuke's lance and True Strike's thread; Entice's tether, which holds its creature while the server drags it in. */
  lance?: THREE.Group; tether?: ReturnType<typeof pathRibbon>;
  /** True Strike has no projectile: its flight runs on its own clock. */
  at?: number; landed?: number;
}
type BurstKind = 'rebuke' | 'seared' | 'harm' | 'wither' | 'entice' | 'guide' | 'summon' | 'farewell' | 'mend';
interface Burst {
  kind: BurstKind; group: THREE.Group; targetId?: string; at: number; duration: number; height: number; position: THREE.Vector3; seed: number;
  rays: { group: THREE.Group; dir: THREE.Vector3; length: number }[]; core?: THREE.Mesh; ring?: THREE.Mesh; pool?: THREE.Mesh; motes?: THREE.Points; wisps?: THREE.Mesh[];
}
/** A True Strike-guided hero: a gold skin of light over each weapon it can hold, a glow at each bare hand, until the blow it guides. */
interface Guided {
  group: THREE.Object3D; skins: THREE.Mesh[]; gilt: THREE.ShaderMaterial; flash: THREE.Mesh;
  hands: { bone: THREE.Object3D; halo: THREE.Mesh }[]; level: number; seed: number; ended?: number; flare?: number;
}
interface Withered { group: THREE.Object3D; surfaces: { material: THREE.MeshStandardMaterial; color: THREE.Color; emissive: THREE.Color }[]; depth: number; seed: number }

/** Each burst's length in game seconds and where on its target it centres (a fraction of the target's height). */
const BURSTS: Readonly<Record<BurstKind, { duration: number; height: number }>> = {
  rebuke: { duration: shepherdSpells.rebuke.impact, height: .55 }, seared: { duration: shepherdSpells.rebuke.seared, height: .55 },
  harm: { duration: .5, height: .5 }, wither: { duration: shepherdSpells.witheringTouch.impact, height: 0 }, entice: { duration: .55, height: .55 },
  guide: { duration: .55, height: .5 }, summon: { duration: 1.1, height: 0 }, farewell: { duration: .9, height: 0 }, mend: { duration: 1.5, height: 0 },
};
/** The props a hero holds: right-hand weapons, the bow, the off hand's weapons. */
export const HELD = ['sword', 'bow', 'off-hand grip'];
/** Seconds the guided weapon takes to light, to go out unspent (the fight ended), and to flare and die at the blow. */
const GUIDED = { rise: .25, fade: .35, flare: .6 };
/** Kinds of event that mark a hero's blow landing, missing or being stopped: the moment True Strike is spent. */
const BLOWS: ReadonlySet<CombatVisualEvent['kind']> = new Set(['strike', 'impact', 'miss', 'block', 'interpose']);
const COLUMNS: ReadonlySet<BurstKind> = new Set(['summon', 'farewell', 'mend']);

/**
 * The Shepherd's spells and Lifebinding Spirit, presentation only. The spirit is a rabbit of warm light that hops at its Shepherd's
 * side while summoned, crouches and brightens while it readies a power, leaps at the foe in a streak of gold on Harm and runs to the
 * Dying on Mend. Each cantrip gathers light in the Shepherd's hand: Rebuke flies as a gold lance and bursts in rays (a sun ring on the
 * ground when it sears for double); Withering Touch coils green rot up its victim, who stays ashen while Withered; Entice pays out a
 * violet tether that holds its creature while the server drags it in; True Strike sends a thread of gold to the ally's weapon.
 */
export class ShepherdEffects {
  private spirits = new Map<string, Spirit>();
  private gathers = new Map<string, Gather>();
  private flights = new Map<number | string, Flight>();
  private bursts: Burst[] = [];
  private withered = new Map<string, Withered>();
  private guided = new Map<string, Guided>();
  /** When each hero last struck, so a spent True Strike flares at its blow. */
  private blows = new Map<string, number>();
  /** Rebuke's double damage is rolled as it leaves: the impact that follows sears. */
  private searing = new Map<string, number>();
  private summons: { heroId: string; at: number }[] = [];
  /** Entice bites waiting for the drawn moment to reach them. */
  private bites: { actorId: string; targetId: string; at: number }[] = [];
  /** Which way each hero faces this frame (the figure's group never turns; its rig does): toward what it casts at, else its heading. */
  private facings = new Map<string, number>();
  private lastTime: number | undefined;
  readonly lights: SpellLight[] = [];
  get visualObjects(): THREE.Object3D[] {
    return [...[...this.spirits.values()].flatMap(spirit => [spirit.root, spirit.trail]), ...[...this.gathers.values()].map(gather => gather.group),
      ...[...this.flights.values()].flatMap(flight => [flight.group, ...flight.tether ? [flight.tether.mesh] : []]), ...this.bursts.map(burst => burst.group),
      ...[...this.guided.values()].flatMap(state => [state.flash, ...state.hands.map(hand => hand.halo)])];
  }
  constructor(private scene: THREE.Scene, private heights: ReadonlyMap<string, number>, private groundHeight: (x: number, z: number) => number = () => 0) {}

  receive(event: CombatVisualEvent, at: number) {
    const attack = event.attackId;
    if (BLOWS.has(event.kind)) this.blows.set(event.actorId, at);
    if (event.kind === 'condition' && event.label === 'REBUKED') this.searing.set(event.targetId, at);
    else if (event.kind === 'impact' && attack === 'rebuke') {
      const seared = (this.searing.get(event.targetId) ?? -Infinity) > at - 3; this.searing.delete(event.targetId);
      this.burst(seared ? 'seared' : 'rebuke', event, at);
    } else if (event.kind === 'miss' && attack === 'rebuke') this.burst('rebuke', event, at, .45);
    else if (event.kind === 'impact' && attack === 'spirit-harm') this.burst('harm', event, at);
    else if (event.kind === 'impact' && attack === 'withering-touch') this.burst('wither', event, at);
    else if ((event.kind === 'impact' || event.kind === 'miss') && attack === 'entice') {
      this.burst('entice', event, at, event.kind === 'miss' ? .5 : 1);
      if (event.kind === 'impact') this.bites.push({ actorId: event.actorId, targetId: event.targetId, at });
    }
    else if (event.kind === 'heal' && event.abilityId === 'spirit-mend') this.burst('mend', event, at);
    else if (event.kind === 'ability' && event.abilityId === 'lifebinding-spirit') this.summons.push({ heroId: event.actorId, at });
    else if (event.kind === 'ability' && event.abilityId === 'true-strike') this.guide(event, at);
  }

  update(snapshot: WorldSnapshot, earlier: WorldSnapshot, alpha: number, positions: ReadonlyMap<string, THREE.Object3D>, ppm = 40) {
    const time = earlier.gameTime + (snapshot.gameTime - earlier.gameTime) * alpha;
    const dt = THREE.MathUtils.clamp(time - (this.lastTime ?? time), 0, .1); this.lastTime = time;
    this.lights.length = 0;
    const shots = new Map<number, ProjectileState>(snapshot.projectiles.map(shot => [shot.id, shot]));
    if (alpha < 1) for (const shot of earlier.projectiles) if (!shots.has(shot.id)) shots.set(shot.id, { ...shot, remaining: 0 });
    const progress = (shot: ProjectileState) => {
      const before = earlier.projectiles.find(item => item.id === shot.id);
      return THREE.MathUtils.clamp(1 - THREE.MathUtils.lerp(before?.remaining ?? shot.duration, shot.remaining, alpha) / shot.duration, 0, 1);
    };
    this.facings.clear();
    for (const hero of snapshot.heroes) {
      const figure = positions.get(hero.id), focus = hero.class.casting?.targetId ?? hero.combat.targetId, mark = focus && focus !== hero.id ? positions.get(focus) : undefined;
      this.facings.set(hero.id, figure && mark ? Math.atan2(mark.position.x - figure.position.x, mark.position.z - figure.position.z)
        : (hero.route.length ? hero.heading : hero.standFacing ?? hero.heading) ?? 0);
    }
    for (const hero of snapshot.heroes) {
      const figure = positions.get(hero.id), summoned = !!hero.class.companion && !hero.health.dead;
      let spirit = this.spirits.get(hero.id);
      if (summoned && figure?.visible && (!spirit || spirit.left !== undefined)) {
        if (spirit) this.release(spirit);
        spirit = this.create(hero.id, figure, time); this.spirits.set(hero.id, spirit);
      }
      if (!spirit) continue;
      if (!summoned && spirit.left === undefined) { spirit.left = time; this.burst('farewell', undefined, time, 1, spirit.root.position); }
      if (!figure?.visible || spirit.left !== undefined && time - spirit.left > LEAVE) { this.release(spirit); this.spirits.delete(hero.id); continue; }
      const shot = [...shots.values()].find(item => item.actorId === hero.id && item.attackId === 'spirit-harm');
      this.pose(spirit, figure, hero.class.companion?.action ?? null, shot && { shot, p: progress(shot) }, positions, time, dt, ppm);
    }
    for (const [id, spirit] of this.spirits) if (!snapshot.heroes.some(hero => hero.id === id)) { this.release(spirit); this.spirits.delete(id); }
    // The summoning flourish rises where the spirit forms, once its Shepherd is drawn.
    this.summons = this.summons.filter(summon => {
      const figure = positions.get(summon.heroId); if (!figure) return time - summon.at < 2;
      this.burst('summon', undefined, summon.at, 1, this.spirits.get(summon.heroId)?.root.position ?? this.beside(figure, this.facings.get(summon.heroId) ?? 0)); return false;
    });
    this.updateGathers(snapshot, earlier, alpha, positions, time, ppm);
    this.updateFlights(shots, progress, positions, time, ppm);
    this.updateBursts(positions, time, ppm);
    this.updateWithered(snapshot, positions, time, dt);
    this.updateGuided(snapshot, positions, time, dt);
  }

  private create(heroId: string, figure: THREE.Object3D, time: number): Spirit {
    const spirit = createSpiritFigure(heroId.length * 1.7), heading = this.facings.get(heroId) ?? 0;
    spirit.root.position.copy(this.beside(figure, heading)); this.scene.add(spirit.root, spirit.trail);
    return { ...spirit, heroId, heading, born: time };
  }
  /** Where it sits when idle: a little behind its Shepherd's left hand. */
  private beside(figure: THREE.Object3D, facing: number) {
    const forward = new THREE.Vector2(Math.sin(facing), Math.cos(facing));
    const x = figure.position.x + forward.y * .95 - forward.x * .35, z = figure.position.z - forward.x * .95 - forward.y * .35;
    return new THREE.Vector3(x, this.groundHeight(x, z), z);
  }
  /** In front of the caster's chest, toward the right hand, where a spell's light gathers and leaves from. */
  private hand(id: string, figure: THREE.Object3D) {
    const facing = this.facings.get(id) ?? 0, height = this.heights.get(id) ?? 2;
    return new THREE.Vector3(figure.position.x + Math.sin(facing) * .34 - Math.cos(facing) * .14, figure.position.y + height * .6, figure.position.z + Math.cos(facing) * .34 + Math.sin(facing) * .14);
  }
  private chest(id: string, figure: THREE.Object3D, at = .55) { return figure.position.clone().setY(figure.position.y + (this.heights.get(id) ?? 2) * at); }

  private pose(spirit: Spirit, figure: THREE.Object3D, readying: CastState | null, dart: { shot: ProjectileState; p: number } | undefined,
    positions: ReadonlyMap<string, THREE.Object3D>, time: number, dt: number, ppm: number) {
    const { root, action } = spirit;
    const appear = THREE.MathUtils.smoothstep(time - spirit.born, 0, APPEAR), leave = spirit.left === undefined ? 1 : 1 - THREE.MathUtils.smoothstep(time - spirit.left, 0, LEAVE);
    const presence = appear * leave;
    const facing = this.facings.get(spirit.heroId) ?? 0;
    let goal = this.beside(figure, facing), speed: number = SPIRIT.speed, face: THREE.Vector3 | undefined;
    const target = readying?.targetId ? positions.get(readying.targetId) : undefined;
    // Mend: it runs to the Dying creature and waits at its side; Harm: it crouches, looking at its prey.
    if (readying?.abilityId === 'spirit-mend' && target) { goal = target.position.clone().add(new THREE.Vector3(.7, 0, .3)); goal.y = this.groundHeight(goal.x, goal.z); face = target.position; }
    if (readying?.abilityId === 'spirit-harm' && target) face = target.position;
    spirit.trail.visible = false;
    if (dart) {
      // The Harm itself: a leap along the server's flight, from where it sprang to the foe, trailing gold.
      if (spirit.dart?.shotId !== dart.shot.id) spirit.dart = { shotId: dart.shot.id, from: root.position.clone() };
      const from = spirit.dart.from, foe = positions.get(dart.shot.targetId)?.position ?? new THREE.Vector3(dart.shot.to.x, 0, dart.shot.to.z);
      const path = (p: number) => new THREE.Vector3().copy(from).lerp(foe, p).setY(this.groundHeight(from.x + (foe.x - from.x) * p, from.z + (foe.z - from.z) * p) + Math.sin(p * Math.PI) * .7);
      root.position.copy(path(dart.p));
      spirit.heading = Math.atan2(foe.x - from.x, foe.z - from.z);
      action.settle = 0; action.bolt = 1; action.hop = .2 + dart.p * .5;
      const tail = Math.max(0, dart.p - 1.1 / Math.max(.5, from.distanceTo(foe))), lift = new THREE.Vector3(0, .22, 0);
      if (dart.p > .02) streakSpirit(spirit, path(tail).add(lift), path(dart.p).add(lift), .9 * presence, time);
    } else {
      spirit.dart = undefined;
      const flat = new THREE.Vector2(goal.x - root.position.x, goal.z - root.position.z), distance = flat.length();
      // Left far behind (after a leap, or when the Shepherd ran), it bounds back at a sprint.
      if (distance > 2) speed = SPIRIT.dart;
      const moving = distance > .12, step = Math.min(distance, speed * dt);
      if (moving) {
        root.position.x += flat.x / distance * step; root.position.z += flat.y / distance * step;
        spirit.heading = turn(spirit.heading, Math.atan2(flat.x, flat.y), dt * 10);
        action.hop = (action.hop + step / (HOP_LENGTH * SPIRIT.scale)) % 1;
      } else if (face) spirit.heading = turn(spirit.heading, Math.atan2(face.x - root.position.x, face.z - root.position.z), dt * 8);
      else spirit.heading = turn(spirit.heading, facing, dt * 2);
      action.settle = THREE.MathUtils.clamp(action.settle + (moving ? -dt : dt) * 6, 0, 1); action.bolt = Math.min(1, speed / SPIRIT.dart);
      root.position.y = this.groundHeight(root.position.x, root.position.z);
      const mood = readying || face ? 'alert' : 'rest';
      if (mood !== action.mood) { action.previous = action.mood; action.mood = mood; action.moodTime = 0; }
    }
    action.idle += dt; action.moodTime += dt;
    root.rotation.y = spirit.heading;
    // Brighter while it gathers itself for a power, brightest at the moment it acts; a slow breath of light otherwise.
    const gather = readying ? 1 - readying.remaining / Math.max(.001, readying.duration) : 0, flare = dart ? 1 : gather;
    const pulse = .9 + Math.sin(time * 2.3 + spirit.heroId.length) * .1;
    shadeSpirit(spirit, presence, flare, pulse, root.position.y - this.groundHeight(root.position.x, root.position.z), time, ppm);
    this.lights.push({ position: root.position.clone().setY(root.position.y + .45), color: new THREE.Color(SPIRIT.light), output: spiritLightOutput(presence, flare) });
  }

  private updateGathers(snapshot: WorldSnapshot, earlier: WorldSnapshot, alpha: number, positions: ReadonlyMap<string, THREE.Object3D>, time: number, ppm: number) {
    for (const hero of snapshot.heroes) {
      const old = earlier.heroes.find(unit => unit.id === hero.id)?.class.casting, next = hero.class.casting, cast = next ?? old;
      const color: number | undefined = cast ? CAST_LIGHT[cast.abilityId] : undefined, figure = positions.get(hero.id);
      if (!cast || color === undefined || !figure?.visible || hero.health.dead) continue;
      let gather = this.gathers.get(hero.id);
      if (!gather || gather.ended !== undefined || gather.color !== color) {
        if (gather) disposeEffect(gather.group);
        const group = new THREE.Group(); group.name = 'shepherd-cast';
        const core = glowBall(color, .16), motes = effectParticles(color, 10, .22, .2, 1); group.add(core, motes);
        this.scene.add(group); gather = { group, core, motes, color, progress: 0 }; this.gathers.set(hero.id, gather);
      }
      const remaining = THREE.MathUtils.lerp(old?.remaining ?? cast.duration, next?.remaining ?? 0, alpha);
      gather.progress = THREE.MathUtils.clamp(1 - remaining / cast.duration, 0, 1);
    }
    for (const [id, gather] of this.gathers) {
      const figure = positions.get(id), casting = snapshot.heroes.find(unit => unit.id === id)?.class.casting ?? earlier.heroes.find(unit => unit.id === id)?.class.casting;
      if (gather.ended === undefined && (!casting || CAST_LIGHT[casting.abilityId] !== gather.color)) gather.ended = time;
      const release = gather.ended === undefined ? 0 : (time - gather.ended) / .28;
      if (!figure?.visible || release >= 1) { disposeEffect(gather.group); this.gathers.delete(id); continue; }
      gather.group.position.copy(this.hand(id, figure));
      // It swells through the cast, and flares as the spell leaves the hand.
      const swell = THREE.MathUtils.smoothstep(gather.progress, 0, .85), flare = release > 0 ? Math.sin(Math.min(1, release) * Math.PI) : 0;
      gather.core.scale.setScalar(.35 + swell * .8 + flare * 1.4);
      const opacity = (.35 + swell * .75) * (1 - release) + flare * .8;
      (gather.core.material as THREE.ShaderMaterial).uniforms.uOpacity.value = opacity;
      shadeEffect(gather.motes, time, opacity * .8, .35 + swell * .45 - release * .3, ppm); gather.motes.rotation.y = time * 2.2;
      this.lights.push({ position: gather.group.position.clone(), color: new THREE.Color(gather.color), output: .05 + swell * .07 + flare * .1 });
    }
  }

  /** True Strike's thread of gold, from the Shepherd's hand to the ally it guides. */
  private guide(event: CombatVisualEvent, at: number) {
    const group = new THREE.Group(), lance = streak(RADIANT.glow, RADIANT.core), head = glowBall(RADIANT.glow, .12); group.add(lance, head); group.visible = false;
    this.scene.add(group);
    this.flights.set(`guide:${event.id}`, { kind: 'guide', actorId: event.actorId, targetId: event.targetId, group, head, lance, from: new THREE.Vector3(), to: new THREE.Vector3(), p: 0, at });
  }
  private launch(kind: 'rebuke' | 'entice', actorId: string, targetId: string, from: THREE.Vector3): Flight {
    const color = kind === 'rebuke' ? RADIANT.glow : ENTICE.color, accent = kind === 'rebuke' ? RADIANT.core : ENTICE.core;
    const group = new THREE.Group(), head = glowBall(color, kind === 'rebuke' ? .15 : .12); group.add(head);
    const flight: Flight = { kind, actorId, targetId, group, head, p: 0, from, to: new THREE.Vector3() };
    if (kind === 'rebuke') { flight.lance = streak(color, accent); group.add(flight.lance); }
    else { flight.tether = pathRibbon(color, .1, 4, accent, 28); this.scene.add(flight.tether.mesh); group.add(effectParticles(ENTICE.core, 6, .1, .1, 1)); }
    this.scene.add(group);
    return flight;
  }
  private updateFlights(shots: Map<number, ProjectileState>, progress: (shot: ProjectileState) => number, positions: ReadonlyMap<string, THREE.Object3D>, time: number, ppm: number) {
    const tethering = (actorId: string, targetId: string) => [...this.flights.values()].some(flight => flight.kind === 'entice' && flight.actorId === actorId && flight.targetId === targetId);
    for (const shot of shots.values()) if ((shot.attackId === 'rebuke' || shot.attackId === 'entice') && !this.flights.has(shot.id) && !(shot.attackId === 'entice' && tethering(shot.actorId, shot.targetId))) {
      const caster = positions.get(shot.actorId);
      this.flights.set(shot.id, this.launch(shot.attackId, shot.actorId, shot.targetId,
        caster ? this.hand(shot.actorId, caster) : new THREE.Vector3(shot.from.x, this.groundHeight(shot.from.x, shot.from.z) + 1.2, shot.from.z)));
    }
    // A bolt too quick for any drawn frame still leaves its tether: the creature is held from the moment it bites.
    this.bites = this.bites.filter(bite => {
      if (bite.at > time) return true;
      if (!tethering(bite.actorId, bite.targetId)) this.flights.set(`entice:${bite.at}:${bite.targetId}`, { ...this.launch('entice', bite.actorId, bite.targetId, new THREE.Vector3()), p: 1, landed: bite.at });
      return false;
    });
    for (const [id, flight] of this.flights) {
      const caster = positions.get(flight.actorId), target = positions.get(flight.targetId), shot = typeof id === 'number' ? shots.get(id) : undefined;
      if (!target?.visible || flight.kind !== 'rebuke' && !caster?.visible) { this.end(id, flight); continue; }
      flight.to.copy(this.chest(flight.targetId, target, flight.kind === 'guide' ? .5 : .55));
      if (flight.kind === 'guide') {
        if (flight.at! > time) continue;
        flight.from.copy(this.hand(flight.actorId, caster!)); flight.p = Math.min(1, (time - flight.at!) / .3);
        if (flight.p >= 1 && flight.landed === undefined) { flight.landed = time; this.burst('guide', undefined, time, 1, undefined, flight.targetId); }
      } else if (shot) flight.p = progress(shot);
      else if (flight.landed === undefined) { flight.landed = time; flight.p = 1; }
      const held = flight.landed === undefined ? 0 : time - flight.landed;
      if (flight.kind !== 'entice' && held > .12 || flight.kind === 'entice' && held > ENTICE.hold) { this.end(id, flight); continue; }
      const head = flight.from.clone().lerp(flight.to, flight.p), fade = flight.kind === 'entice' ? 1 - THREE.MathUtils.smoothstep(held / ENTICE.hold, .55, 1) : 1 - held / .12;
      flight.group.visible = true;
      flight.head.position.copy(head); flight.head.scale.setScalar(1 + Math.sin(time * 31) * .12);
      (flight.head.material as THREE.ShaderMaterial).uniforms.uOpacity.value = (flight.landed === undefined ? 1.4 : .6) * fade;
      for (const child of flight.group.children) if (child instanceof THREE.Points) { child.position.copy(head); shadeEffect(child, time, fade, .6, ppm); }
      if (flight.lance) {
        const tail = flight.from.clone().lerp(flight.to, Math.max(0, flight.p - (flight.kind === 'rebuke' ? 1.3 : 2.4) / Math.max(.5, flight.from.distanceTo(flight.to))));
        aim(flight.lance, tail, head, flight.kind === 'rebuke' ? .26 : .16); shadeEffect(flight.lance, time, fade);
      }
      if (flight.tether) {
        // In flight it pays out behind the bolt; once it bites, it holds taut between them while the creature is dragged, then lets go.
        flight.tether.update(this.hand(flight.actorId, caster!), flight.landed === undefined ? head : flight.to, time, false, Number(id), 1);
        shadeEffect(flight.tether.mesh, time, .6 * fade, flight.landed === undefined ? flight.p : (time * 1.4) % 1);
      }
      const color = flight.kind === 'entice' ? ENTICE.color : RADIANT.glow;
      this.lights.push({ position: head, color: new THREE.Color(color), output: (flight.kind === 'rebuke' ? .14 : .08) * fade });
    }
  }
  private end(id: number | string, flight: Flight) { disposeEffect(flight.group); if (flight.tether) disposeEffect(flight.tether.mesh); this.flights.delete(id); }

  private burst(kind: BurstKind, event: CombatVisualEvent | undefined, at: number, scale = 1, place?: THREE.Vector3, targetId = event?.targetId) {
    const group = new THREE.Group(); group.name = `shepherd-${kind}`; group.visible = false;
    const { duration, height } = BURSTS[kind], seed = event?.id ?? this.bursts.length + at;
    const position = place?.clone() ?? new THREE.Vector3(event?.to.x ?? 0, 0, event?.to.z ?? 0);
    if (!place) position.y = this.groundHeight(position.x, position.z);
    const burst: Burst = { kind, group, targetId: place ? undefined : targetId, at, duration, height, position, seed, rays: [] };
    const rays = (count: number, length: number, color: number, accent: number) => rayDirections(count, seed).forEach((dir, i) => {
      const ray = streak(color, accent, 1); group.add(ray); burst.rays.push({ group: ray, dir, length: length * scale * (.75 + (Math.sin(seed * 5.3 + i * 7.1) * .5 + .5) * .5) });
    });
    if (kind === 'rebuke' || kind === 'seared') {
      rays(kind === 'seared' ? 12 : shepherdSpells.rebuke.rays, kind === 'seared' ? 1 : .62, RADIANT.glow, RADIANT.core);
      burst.core = glowBall(RADIANT.glow, .32 * scale); burst.motes = effectParticles(RADIANT.accent, kind === 'seared' ? 16 : 10, .35, 1.2);
      if (kind === 'seared') { burst.ring = disc(RADIANT.glow, 1); burst.pool = disc(RADIANT.glow, 0); }
    } else if (kind === 'harm' || kind === 'guide') {
      rays(kind === 'harm' ? 7 : 5, kind === 'harm' ? .68 : .32, SPIRIT.glow, RADIANT.core);
      burst.core = glowBall(SPIRIT.glow, kind === 'harm' ? .34 : .18); if (kind === 'harm') burst.motes = effectParticles(SPIRIT.light, 8, .3, .8);
    } else if (kind === 'entice') {
      burst.core = glowBall(ENTICE.color, .26 * scale); burst.motes = effectParticles(ENTICE.core, 8, .28, .5, 1);
    } else if (kind === 'wither') {
      // Rot coils up the body from the ground, shedding spores.
      burst.wisps = Array.from({ length: 3 }, (_, i) => { const mesh = new THREE.Mesh(spiralRibbon(.42, 1, 1.25, .14, i * TAU / 3), effectMaterial(NECROTIC.glow, NECROTIC.accent, 1)); group.add(mesh); return mesh; });
      burst.core = glowBall(NECROTIC.glow, .3); burst.motes = effectParticles(NECROTIC.accent, 14, .45, 1.4); burst.pool = disc(NECROTIC.glow, 0);
    } else if (kind === 'summon' || kind === 'farewell') {
      burst.core = glowBall(SPIRIT.glow, .4); burst.motes = effectParticles(SPIRIT.light, kind === 'summon' ? 16 : 10, .35, 1.3);
      if (kind === 'summon') { burst.ring = disc(SPIRIT.glow, 1); burst.pool = disc(SPIRIT.glow, 0); }
    } else {
      // Mend: a column of gold rising through the patient, a ring of light opening at its feet.
      burst.core = glowBall(SPIRIT.glow, .5); burst.motes = effectParticles(SPIRIT.light, 16, .45, 1.8); burst.ring = disc(SPIRIT.glow, 1); burst.pool = disc(SPIRIT.glow, 0);
    }
    for (const part of [burst.core, burst.motes, burst.ring, burst.pool]) if (part) group.add(part);
    this.scene.add(group); this.bursts.push(burst);
    while (this.bursts.length > 32) disposeEffect(this.bursts.shift()!.group);
  }
  private updateBursts(positions: ReadonlyMap<string, THREE.Object3D>, time: number, ppm: number) {
    this.bursts = this.bursts.filter(burst => {
      const p = (time - burst.at) / burst.duration;
      if (p >= 1) { disposeEffect(burst.group); return false; }
      if (p < 0) return true;
      const target = burst.targetId ? positions.get(burst.targetId) : undefined, tall = burst.targetId ? this.heights.get(burst.targetId) ?? 2 : 1;
      if (target) burst.position.copy(target.position);
      burst.group.position.copy(burst.position); burst.group.visible = true;
      const center = burst.height * tall, fade = Math.pow(1 - p, 1.5) * THREE.MathUtils.smoothstep(p, 0, .06), out = 1 - Math.pow(1 - Math.min(1, p * 2.6), 3);
      for (const ray of burst.rays) {
        const from = new THREE.Vector3(0, center, 0).addScaledVector(ray.dir, .08), to = new THREE.Vector3(0, center, 0).addScaledVector(ray.dir, .12 + ray.length * out);
        aim(ray.group, from, to, .16); shadeEffect(ray.group, time, fade * 1.2);
      }
      if (burst.core) {
        const material = burst.core.material as THREE.ShaderMaterial;
        if (COLUMNS.has(burst.kind)) {
          // A column of light: rising as the spirit forms or the patient is Mended, lifting away as the spirit leaves.
          burst.core.position.y = burst.kind === 'farewell' ? .25 + p * .9 : .2 + p * (burst.kind === 'mend' ? tall * .75 : 1.1);
          burst.core.scale.set(.8 + p * .4, 1.6 + p * .8, .8 + p * .4);
          material.uniforms.uOpacity.value = fade * (burst.kind === 'mend' ? 1.1 : 1.3);
        } else {
          burst.core.position.y = center; burst.core.scale.setScalar(.45 + out * (burst.kind === 'seared' ? 1.2 : .9));
          material.uniforms.uOpacity.value = Math.pow(1 - p, 2.4) * (burst.kind === 'seared' ? 1.5 : 1.2);
        }
      }
      if (burst.motes) { burst.motes.position.y = COLUMNS.has(burst.kind) || burst.kind === 'wither' ? .1 : center - .2; shadeEffect(burst.motes, time, fade * 1.1, p, ppm); }
      if (burst.ring) {
        burst.ring.position.y = .04; burst.ring.scale.setScalar(burst.kind === 'seared' ? .3 + out * .8 : burst.kind === 'summon' ? .2 + out * .6 : .25 + out * .65);
        (burst.ring.material as THREE.ShaderMaterial).uniforms.uOpacity.value = fade * (burst.kind === 'seared' ? .5 : .65);
      }
      if (burst.pool) {
        burst.pool.position.y = .03; burst.pool.scale.setScalar(burst.kind === 'seared' ? .75 : burst.kind === 'wither' ? .8 + out * .3 : .8);
        (burst.pool.material as THREE.ShaderMaterial).uniforms.uOpacity.value = fade * (burst.kind === 'wither' ? .55 : burst.kind === 'seared' ? .3 : .45);
      }
      burst.wisps?.forEach((wisp, i) => {
        const grow = THREE.MathUtils.smoothstep(p, 0, .45);
        wisp.scale.set(.85 + p * .3, Math.max(.01, tall * .85 * grow), .85 + p * .3); wisp.rotation.y = -time * 2.4 - i * .4;
        shadeEffect(wisp, time, fade * 1.6, p);
      });
      const color = burst.kind === 'wither' ? NECROTIC.glow : burst.kind === 'entice' ? ENTICE.color : burst.kind === 'rebuke' || burst.kind === 'seared' ? RADIANT.glow : SPIRIT.light;
      const output = burst.kind === 'seared' ? .26 : burst.kind === 'rebuke' ? .18 : burst.kind === 'mend' || burst.kind === 'summon' ? .16 : .12;
      this.lights.push({ position: burst.position.clone().setY(burst.position.y + Math.max(.4, center)), color: new THREE.Color(color), output: output * fade });
      return true;
    });
  }

  /** A Withered creature turns ashen, with a faint rot glow beneath its surfaces, for as long as the server says it counts as undead. */
  private updateWithered(snapshot: WorldSnapshot, positions: ReadonlyMap<string, THREE.Object3D>, time: number, dt: number) {
    const ash = new THREE.Color(WITHERED.ash), rot = new THREE.Color(WITHERED.rot), units = [...snapshot.enemies, ...snapshot.heroes];
    const marked = new Set(units.filter(unit => unit.combat.withered && !unit.health.dead).map(unit => unit.id));
    for (const id of marked) { const group = positions.get(id); if (group && !this.withered.has(id)) this.withered.set(id, this.wither(group, id)); }
    for (const [id, state] of this.withered) {
      if (positions.get(id) !== state.group || units.some(unit => unit.id === id && unit.health.dead)) { this.unwither(id); continue; }
      state.depth = THREE.MathUtils.clamp(state.depth + (marked.has(id) ? dt : -dt) / .35, 0, 1);
      if (state.depth <= 0 && !marked.has(id)) { this.unwither(id); continue; }
      const pulse = .75 + Math.sin(time * 2.1 + state.seed) * .25;
      for (const surface of state.surfaces) {
        surface.material.color.copy(surface.color).lerp(ash, state.depth * WITHERED.depth);
        surface.material.emissive.copy(surface.emissive).lerp(rot, state.depth * pulse * WITHERED.sheen);
      }
    }
  }
  private wither(group: THREE.Object3D, id: string): Withered {
    ownFigureMaterials(group);
    const surfaces: Withered['surfaces'] = [], seen = new Set<THREE.Material>();
    group.traverse(node => {
      if (!(node instanceof THREE.Mesh)) return;
      for (const material of [node.material as THREE.Material | THREE.Material[]].flat()) {
        if (seen.has(material) || !(material instanceof THREE.MeshStandardMaterial)) continue;
        seen.add(material); surfaces.push({ material, color: material.color.clone(), emissive: material.emissive.clone() });
      }
    });
    return { group, surfaces, depth: 0, seed: id.length * 1.3 };
  }
  private unwither(id: string) {
    const state = this.withered.get(id); if (!state) return;
    for (const { material, color, emissive } of state.surfaces) { material.color.copy(color); material.emissive.copy(emissive); }
    shareFigureMaterials(state.group); this.withered.delete(id);
  }

  /**
   * True Strike: the guided hero's weapon glows a gently pulsing warm gold (a bow, the bow; bare-handed, the hands), casting a little real light
   * on the hero and the ground. When the guided blow comes it flares once and goes out; spent unused, it simply fades.
   */
  private updateGuided(snapshot: WorldSnapshot, positions: ReadonlyMap<string, THREE.Object3D>, time: number, dt: number) {
    const gold = new THREE.Color(RADIANT.glow);
    const marked = new Set(snapshot.heroes.filter(hero => hero.combat.trueStrike && !hero.health.dead).map(hero => hero.id));
    for (const id of marked) {
      const group = positions.get(id), state = this.guided.get(id);
      if (group && !state) this.guided.set(id, this.gild(group, id));
      else if (state?.ended !== undefined) state.ended = state.flare = undefined;
    }
    for (const [id, state] of this.guided) {
      if (positions.get(id) !== state.group || !state.group.visible || !snapshot.heroes.some(hero => hero.id === id && !hero.health.dead)) { this.ungild(id); continue; }
      if (marked.has(id)) state.level = Math.min(1, state.level + dt / GUIDED.rise);
      else {
        state.ended ??= time;
        const blow = this.blows.get(id);
        if (state.flare === undefined && blow !== undefined && blow > state.ended - 1 && blow <= time) state.flare = Math.max(blow, state.ended);
        // Spent, it flares out at its blow (whose event may come a moment after the state clears); unspent, it dims away.
        if (state.flare === undefined) {
          if (time - state.ended > .5) state.level = Math.max(0, state.level - dt / GUIDED.fade);
          if (state.level <= 0) { this.ungild(id); continue; }
        } else if (time - state.flare > GUIDED.flare) { this.ungild(id); continue; }
      }
      const since = state.flare === undefined ? undefined : time - state.flare;
      const flare = since === undefined ? 0 : Math.exp(-since * 7);
      const held = since === undefined ? 1 : Math.max(0, 1 - since / .2);
      const glow = state.level * held * (.8 + Math.sin(time * 3.2 + state.seed) * .2) + flare * 2.4;
      const shining: THREE.Vector3[] = [];
      state.gilt.uniforms.uOpacity.value = Math.min(2.2, glow * .85);
      for (const skin of state.skins) if (shown(skin, state.group)) shining.push(skin.getWorldPosition(new THREE.Vector3()));
      const bare = !shining.length;
      for (const { bone, halo } of state.hands) {
        halo.visible = bare; if (!bare) continue;
        bone.getWorldPosition(halo.position); halo.scale.setScalar(1 + flare * .6);
        (halo.material as THREE.ShaderMaterial).uniforms.uOpacity.value = glow * 1.2;
        shining.push(halo.position.clone());
      }
      if (!shining.length) continue;
      const at = shining.reduce((sum, point) => sum.add(point), new THREE.Vector3()).divideScalar(shining.length);
      // The blow's flare: a burst of the same light round the weapon as it strikes.
      state.flash.visible = flare > .01; state.flash.position.copy(at); state.flash.scale.setScalar(.8 + (since ?? 0) * 2);
      (state.flash.material as THREE.ShaderMaterial).uniforms.uOpacity.value = flare * 1.5;
      this.lights.push({ position: at, color: gold.clone(), output: .03 * state.level * held + flare * .16 });
    }
  }
  private gild(group: THREE.Object3D, id: string): Guided {
    const gilt = giltMaterial(RADIANT.glow), skins: THREE.Mesh[] = [];
    for (const name of HELD) group.getObjectByName(name)?.traverse(node => {
      if (!(node instanceof THREE.Mesh) || node.name.endsWith('-gilt')) return;
      const skin = new THREE.Mesh(node.geometry, gilt); skin.name = 'true-strike-gilt'; skin.renderOrder = 2; node.add(skin); skins.push(skin);
    });
    const flash = glowBall(RADIANT.glow, .3); flash.name = 'true-strike-flare'; flash.visible = false; this.scene.add(flash);
    const hands = ['hand.L', 'hand.R'].flatMap(name => {
      const bone = group.getObjectByName(name); if (!bone) return [];
      const halo = glowBall(RADIANT.glow, .1); halo.name = 'true-strike-hand'; halo.visible = false; this.scene.add(halo);
      return [{ bone, halo }];
    });
    return { group, skins, gilt, flash, hands, level: 0, seed: id.length * 1.9 };
  }
  private ungild(id: string) {
    const state = this.guided.get(id); if (!state) return;
    for (const skin of state.skins) skin.removeFromParent();
    state.gilt.dispose(); disposeEffect(state.flash);
    for (const { halo } of state.hands) disposeEffect(halo);
    this.guided.delete(id);
  }

  private release(spirit: Spirit) { releaseSpiritFigure(spirit); }
  clear() {
    for (const spirit of this.spirits.values()) this.release(spirit);
    for (const gather of this.gathers.values()) disposeEffect(gather.group);
    for (const [id, flight] of this.flights) this.end(id, flight);
    for (const burst of this.bursts) disposeEffect(burst.group);
    for (const id of [...this.withered.keys()]) this.unwither(id);
    for (const id of [...this.guided.keys()]) this.ungild(id);
    this.blows.clear();
    this.spirits.clear(); this.gathers.clear(); this.bursts = []; this.searing.clear(); this.summons = []; this.bites = []; this.lights.length = 0; this.lastTime = undefined;
  }
}

/** Whether a figure's part is drawn: it and every parent up to the figure are visible. */
export function shown(node: THREE.Object3D, root: THREE.Object3D) {
  for (let at: THREE.Object3D | null = node; at && at !== root; at = at.parent) if (!at.visible) return false;
  return true;
}

/** Turns `from` toward `to` by at most `rate` radians, the short way round. */
function turn(from: number, to: number, rate: number) {
  const delta = ((to - from + Math.PI) % TAU + TAU) % TAU - Math.PI;
  return from + Math.sign(delta) * Math.min(Math.abs(delta), rate);
}
